import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  Sun, 
  Utensils, 
  Baby,
  Heart,
  PlayCircle,
  Shield,
  Moon,
  ArrowRight,
  CheckCircle,
  MapPin,
  Users
} from 'lucide-react';

const Program = () => {
  const dailySchedule = [
    {
      time: "7:00–9:00",
      activity: "Free Play",
      description: "Children arrive and engage in self-directed play activities with building blocks, sensory toys, and exploration stations",
      icon: PlayCircle,
      color: "bg-gradient-to-br from-yellow-400 to-orange-400",
      bgColor: "bg-yellow-50",
      detail: "Welcoming arrival time with open-ended play opportunities"
    },
    {
      time: "9:00–9:30", 
      activity: "Diapering & Handwashing",
      description: "Hygienic care routines with dignity and respect, building independence and self-care skills",
      icon: Baby,
      color: "bg-gradient-to-br from-green-400 to-teal-400",
      bgColor: "bg-green-50",
      detail: "Gentle care with learning opportunities"
    },
    {
      time: "9:30–10:00",
      activity: "Morning Snack",
      description: "Healthy, toddler-friendly snacks provided by the center - fresh fruits, crackers, cheese, and vegetables",
      icon: Utensils,
      color: "bg-gradient-to-br from-blue-400 to-indigo-400",
      bgColor: "bg-blue-50",
      detail: "Nutritious fuel for growing bodies and minds"
    },
    {
      time: "10:00–10:30",
      activity: "Circle/Activity Time",
      description: "Group activities, songs, stories, and learning experiences that build language and social skills",
      icon: Heart,
      color: "bg-gradient-to-br from-purple-400 to-pink-400",
      bgColor: "bg-purple-50",
      detail: "Building community through shared experiences"
    },
    {
      time: "10:30–11:30",
      activity: "Outdoor Play",
      description: "Active play in our secure outdoor space with climbing, running, and nature exploration (weather permitting)",
      icon: Sun,
      color: "bg-gradient-to-br from-orange-400 to-red-400",
      bgColor: "bg-orange-50",
      detail: "Fresh air and physical development"
    },
    {
      time: "11:30–12:00",
      activity: "Wash & Diaper",
      description: "Preparation for lunch with clean hands and fresh diapers - teaching healthy hygiene habits",
      icon: Baby,
      color: "bg-gradient-to-br from-pink-400 to-rose-400",
      bgColor: "bg-pink-50",
      detail: "Clean and comfortable before mealtime"
    },
    {
      time: "12:00–1:00",
      activity: "Lunch Time",
      description: "Nut-free lunches brought from home, enjoyed together in a family-style dining experience",
      icon: Utensils,
      color: "bg-gradient-to-br from-red-400 to-pink-400",
      bgColor: "bg-red-50",
      detail: "Social eating with balanced nutrition"
    },
    {
      time: "1:00–3:00",
      activity: "Nap/Quiet Time",
      description: "Peaceful rest period with safe sleep checks every 10-15 minutes in our cozy sleep room",
      icon: Moon,
      color: "bg-gradient-to-br from-indigo-400 to-purple-400",
      bgColor: "bg-indigo-50",
      detail: "Restorative sleep for healthy development"
    },
    {
      time: "3:00–3:30",
      activity: "Afternoon Snack",
      description: "Second healthy snack to refuel for afternoon activities - variety of nutritious options daily",
      icon: Utensils,
      color: "bg-gradient-to-br from-teal-400 to-green-400",
      bgColor: "bg-teal-50",
      detail: "Energy boost for afternoon adventures"
    },
    {
      time: "3:30–4:00",
      activity: "Circle Time",
      description: "Afternoon group activities, interactive learning, music, and movement experiences",
      icon: Heart,
      color: "bg-gradient-to-br from-cyan-400 to-blue-400",
      bgColor: "bg-cyan-50",
      detail: "Learning through play and interaction"
    },
    {
      time: "4:00–6:00",
      activity: "Outdoor Play / Free Play",
      description: "Active outdoor time or indoor exploration centers until pickup - choice-based learning",
      icon: Sun,
      color: "bg-gradient-to-br from-violet-400 to-purple-400",
      bgColor: "bg-violet-50",
      detail: "Flexible play until family reunion"
    }
  ];

  const nutritionHighlights = [
    {
      title: "Two Daily Snacks",
      description: "We provide two nutritious snacks daily, including fresh fruits, whole grain crackers, cheese, and vegetables."
    },
    {
      title: "Family-Provided Lunch",
      description: "Parents send well-balanced, nut-free lunches with items from all major food groups."
    },
    {
      title: "Breastfeeding Support",
      description: "We support breastfeeding families and accommodate feeding schedules and preferences."
    },
    {
      title: "Special Dietary Needs",
      description: "We accommodate dietary restrictions including vegetarian, halal, and gluten-free diets."
    },
    {
      title: "Food Safe Practices",
      description: "All food is prepared in accordance with Food Safe regulations by trained staff."
    },
    {
      title: "Positive Mealtime Experience",
      description: "Children are never forced to eat. We model healthy eating habits and create positive food experiences."
    }
  ];

  const activePlayFeatures = [
    "Minimum 120 minutes of active play daily",
    "Indoor and outdoor physical activities",
    "Free play, obstacle courses, and dancing",
    "Guided movement games for gross motor development",
    "Daily outdoor play (weather permitting)",
    "Screen-free environment for children"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-green-500 text-white text-base px-4 py-2 mb-6">
              Ages 0-36 Months
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-purple-600 mb-6">
              Our Daily Program
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              A thoughtfully designed daily routine that balances active play, quiet learning, 
              and nurturing care to support your child's healthy development.
            </p>
          </div>
        </div>
      </section>

      {/* Daily Schedule */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-blue-500 text-white text-base px-4 py-2 mb-6">
              Daily Routine
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-purple-600 mb-6">
              A Day at Maple Kids Academy
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Our daily routine provides structure while remaining flexible and child-led when needed. 
              Every activity is designed to support development through play, exploration, and connection.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {dailySchedule.map((item, index) => (
                <Card key={index} className={`group hover:shadow-2xl transition-all duration-500 ${item.bgColor} border-2 border-transparent hover:border-purple-300 relative overflow-hidden transform hover:-translate-y-2`}>
                  <div className={`absolute top-0 left-0 w-full h-2 ${item.color}`}></div>
                  <CardHeader className="pb-4 pt-6">
                    <div className="flex items-start space-x-4">
                      <div className={`p-4 ${item.color} text-white rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-xl flex-shrink-0`}>
                        <item.icon className="h-8 w-8" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <CardTitle className="text-xl font-bold text-purple-700 group-hover:text-purple-800 transition-colors">{item.activity}</CardTitle>
                          <Badge className="bg-purple-500 text-white text-sm px-3 py-1 rounded-full shadow">{item.time}</Badge>
                        </div>
                        <p className="text-xs text-purple-600 font-medium mb-3 italic">{item.detail}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-gray-700 leading-relaxed text-sm">{item.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="w-full bg-purple-100 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-1000 group-hover:w-full" 
                          style={{width: `${((index + 1) / dailySchedule.length) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="text-center mt-20">
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 backdrop-blur-sm p-10 rounded-3xl max-w-3xl mx-auto shadow-2xl border-2 border-purple-200">
              <div className="mb-6">
                <div className="inline-flex items-center space-x-2 bg-purple-500 text-white px-6 py-3 rounded-full shadow-lg">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-bold">Flexible & Child-Led</span>
                </div>
              </div>
              <p className="text-purple-700 font-medium text-xl mb-8 leading-relaxed">
                ✨ Our schedule adapts to each child's needs and interests while providing the security of routine ✨
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white/70 p-4 rounded-xl shadow-sm">
                  <Heart className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-purple-700">Individual Attention</p>
                </div>
                <div className="bg-white/70 p-4 rounded-xl shadow-sm">
                  <Shield className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-purple-700">Safe Environment</p>
                </div>
                <div className="bg-white/70 p-4 rounded-xl shadow-sm">
                  <Sun className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-purple-700">Outdoor Focus</p>
                </div>
              </div>
              <Button asChild className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-10 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all text-lg font-bold">
                <Link to="/enroll">
                  Join Our Daily Adventures
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Nutrition Program */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Nutrition Program
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Maple Kids Academy values nutrition as an essential part of a child's overall development. 
                Our approach creates positive food experiences while accommodating each family's needs and preferences.
              </p>

              <div className="space-y-6">
                {nutritionHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-navy-primary mb-1">{highlight.title}</h4>
                      <p className="text-gray-600 text-sm">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                <h4 className="font-semibold text-navy-primary mb-2">Mealtime Philosophy</h4>
                <p className="text-gray-600 text-sm">
                  During mealtimes, children sit together at low tables, developing both social skills and 
                  self-feeding independence. Our educators sit alongside children to model healthy eating habits 
                  and encourage positive food experiences. Children are never forced to eat and food is never 
                  used as punishment or reward.
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1554343594-1c9d305bd51f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHx0b2RkbGVyc3xlbnwwfHx8fDE3NTYwMTU2NTd8MA&ixlib=rb-4.1.0&q=85" 
                alt="Toddler with colorful crayons during snack time" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-rainbow-green text-white p-4 rounded-full shadow-lg">
                <Utensils className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Active Play & Screen-Free Environment */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1440288736878-766bd5839edb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxwbGF5aW5nfGVufDB8fHx8MTc1NjAxNTY3MXww&ixlib=rb-4.1.0&q=85" 
                alt="Child with bubbles during joyful play activity" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -top-6 -left-6 bg-rainbow-orange text-white p-4 rounded-full shadow-lg">
                <Sun className="h-8 w-8" />
              </div>
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Active Play & Movement
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We believe movement and hands-on exploration are key to early learning. 
                Children at Maple Kids Academy participate in at least 120 minutes of active play daily, 
                supporting gross motor development, coordination, and confidence.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {activePlayFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">{feature}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-0">
                  <CardContent className="p-6 text-center">
                    <Sun className="h-12 w-12 text-blue-primary mx-auto mb-3" />
                    <h4 className="font-semibold text-navy-primary mb-2">Outdoor Play</h4>
                    <p className="text-gray-600 text-sm">
                      Daily outdoor play in all weather conditions, with indoor alternatives on extreme weather days.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-0">
                  <CardContent className="p-6 text-center">
                    <Shield className="h-12 w-12 text-accent-orange mx-auto mb-3" />
                    <h4 className="font-semibold text-navy-primary mb-2">Screen-Free</h4>
                    <p className="text-gray-600 text-sm">
                      No screens for children - only internal security cameras used for supervision and safety.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safe Sleep & Supervision */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Safe Sleep & Supervision
            </h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              Children at Maple Kids Academy are always supervised with care, attentiveness, 
              and a deep understanding of their developmental needs.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Shield className="h-12 w-12 text-blue-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-navy-primary mb-2">Constant Supervision</h3>
                  <p className="text-gray-600 text-sm">
                    Visual and auditory supervision at all times, including during rest periods, 
                    diapering, and transitions between activities.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Moon className="h-12 w-12 text-blue-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-navy-primary mb-2">Safe Sleep Checks</h3>
                  <p className="text-gray-600 text-sm">
                    To promote safe sleep, our educators check each sleeping child every 10–15 minutes 
                    during nap time.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Heart className="h-12 w-12 text-blue-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-navy-primary mb-2">Nurturing Relationships</h3>
                  <p className="text-gray-600 text-sm">
                    Through nurturing presence, our staff build strong relationships with each child, 
                    fostering trust and emotional safety.
                  </p>
                </CardContent>
              </Card>
            </div>

            <p className="text-lg text-gray-600 mt-12 leading-relaxed">
              We prioritize child safety through thoughtful room layouts and low educator-to-child ratios, 
              ensuring that every child receives close attention. Our video monitoring system provides 
              an added layer of security and accountability.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-purple-600 to-pink-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Come Visit Our Happy Place!
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-yellow-300" />
                  <div>
                    <p className="font-bold text-lg">10681 248 St, Maple Ridge, BC</p>
                    <p className="text-purple-100">Easy parking for families</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <Clock className="h-5 w-5 mt-1 flex-shrink-0 text-green-300" />
                  <div>
                    <p className="font-bold text-lg">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-purple-100">Closed weekends and holidays</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/contact">Book Your Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white/20 hover:bg-white/30 text-white border-2 border-white hover:border-yellow-300 font-bold px-8 py-4 rounded-full transition-all"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  Why Choose Maple Kids?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Small groups, big love</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                      <Shield className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Licensed & insured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Every child is welcome</span>
                  </div>
                </div>
                
                <div className="mt-6 text-center bg-white/20 p-4 rounded-xl">
                  <p className="text-sm font-medium">Ready to join our family?</p>
                  <p className="text-xs text-purple-100 mt-1">Tours available by appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Program;